import { channelApiUrl } from "./channel";
export const SUPPORT_DRAFT_KEY = "hs-support-draft-v2";
export const SUPPORT_OUTBOX_KEY = "hs-support-outbox-v1";
export const supportCategories = ["bug", "accessibility", "safety", "appeal", "game_source", "other"] as const;
export type SupportCategory = typeof supportCategories[number];
export type SupportDraft = { category: SupportCategory; body: string };
export type SupportOutbox = SupportDraft & { idempotencyKey: string; queuedAt: string; attempts: number };
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem"> & { flush?: () => Promise<void> };

function category(value: unknown): SupportCategory {
  return supportCategories.includes(value as SupportCategory) ? value as SupportCategory : "bug";
}

function body(value: unknown) {
  return typeof value === "string" ? value.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "").slice(0, 2_000) : "";
}

export function loadSupportDraft(storage: StorageLike): SupportDraft {
  try {
    const parsed = JSON.parse(storage.getItem(SUPPORT_DRAFT_KEY) ?? "null") as Partial<SupportDraft> | null;
    if (parsed) return { category: category(parsed.category), body: body(parsed.body) };
  } catch { throw new Error("문의 초안을 읽지 못했어요. 원본은 보존했어요."); }
  return { category: "bug", body: body(storage.getItem("hs-support-draft")) };
}

export function saveSupportDraft(storage: StorageLike, draft: SupportDraft) {
  const safe = { category: category(draft.category), body: body(draft.body) };
  storage.setItem(SUPPORT_DRAFT_KEY, JSON.stringify(safe));
  return safe;
}

export function queueSupportRequest(storage: StorageLike, draft: SupportDraft, now = new Date().toISOString(), idempotencyKey: string = crypto.randomUUID()) {
  const safe = saveSupportDraft(storage, draft);
  if (safe.body.trim().length < 10) throw new Error("문의 내용은 10자 이상 적어 주세요.");
  const existing = loadSupportOutbox(storage);
  if (existing && existing.category === safe.category && existing.body === safe.body) return existing;
  const queued: SupportOutbox = { ...safe, idempotencyKey, queuedAt: now, attempts: 0 };
  storage.setItem(SUPPORT_OUTBOX_KEY, JSON.stringify(queued));
  return queued;
}

export function loadSupportOutbox(storage: StorageLike): SupportOutbox | null {
  try {
    const parsed = JSON.parse(storage.getItem(SUPPORT_OUTBOX_KEY) ?? "null") as Partial<SupportOutbox> | null;
    if (!parsed || typeof parsed.idempotencyKey !== "string" || typeof parsed.queuedAt !== "string") return null;
    return { category: category(parsed.category), body: body(parsed.body), idempotencyKey: parsed.idempotencyKey.slice(0, 100), queuedAt: parsed.queuedAt, attempts: Math.max(0, Math.round(Number(parsed.attempts) || 0)) };
  } catch { throw new Error("이전 문의의 전달 상태를 읽지 못했어요. 원본은 보존했어요."); }
}

export async function deliverSupportOutbox(storage: StorageLike, request: typeof fetch = fetch): Promise<{ status: "sent"; receiptId: string; cleanupPending?: true } | { status: "wait_external"; reason: string }> {
  const queued = loadSupportOutbox(storage);
  if (!queued) return { status: "wait_external", reason: "전송할 문의가 없어요." };
  storage.setItem(SUPPORT_OUTBOX_KEY, JSON.stringify({ ...queued, attempts: queued.attempts + 1 }));
  await storage.flush?.();
  try {
    const response = await request(channelApiUrl("/api/v1/support"), {
      method: "POST",
      headers: { "content-type": "application/json", "x-idempotency-key": queued.idempotencyKey },
      body: JSON.stringify({ category: queued.category, body: queued.body, build: import.meta.env.VITE_BUILD_VERSION ?? "local" }),
    });
    const payload = await response.json().catch(() => null) as { ok?: boolean; data?: { receiptId?: string }; error?: { message?: string } } | null;
    if (!response.ok || !payload?.ok || !payload.data?.receiptId) return { status: "wait_external", reason: payload?.error?.message ?? "문의 전달 채널이 아직 연결되지 않았어요. 초안과 재시도 키는 이 기기에 남아 있습니다." };
    try {
      // Keep the retry key until drafts are durably cleared. A cleanup failure is not a failed delivery.
      storage.removeItem(SUPPORT_DRAFT_KEY);
      await storage.flush?.();
      storage.removeItem("hs-support-draft");
      await storage.flush?.();
      storage.removeItem(SUPPORT_OUTBOX_KEY);
      await storage.flush?.();
    } catch { return { status: "sent", receiptId: payload.data.receiptId, cleanupPending: true }; }
    return { status: "sent", receiptId: payload.data.receiptId };
  } catch {
    return { status: "wait_external", reason: "네트워크에 연결되지 않았어요. 자동 전송하지 않고 사용자가 다시 누를 때 재시도합니다." };
  }
}
